import React, { useEffect, useState } from 'react';
import { Card, Typography, Space, Spin, Row, Col } from 'antd';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { analyticsApi } from '../services/api';

const { Title } = Typography;

interface PrefectureData {
  name: string;
  value: number;
  itemStyle?: {
    areaColor: string;
  };
}

interface JapanMapChartProps {
  selectedDate: string;
}

const JapanMapChart: React.FC<JapanMapChartProps> = ({ selectedDate }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [mapData, setMapData] = useState<PrefectureData[]>([]);
  const [mapLoaded, setMapLoaded] = useState<boolean>(false);
  const [chartInstance, setChartInstance] = useState<any>(null);
  const [top3Regions, setTop3Regions] = useState<string[]>([]);

  // 日本都道府县名称映射（ECharts使用的标准名称）
  const prefectureMapping: { [key: string]: string } = {
    '北海道': '北海道',
    '青森県': '青森',
    '青森': '青森',
    '岩手県': '岩手',
    '岩手': '岩手',
    '宮城県': '宮城',
    '宮城': '宮城',
    '秋田県': '秋田',
    '秋田': '秋田',
    '山形県': '山形',
    '山形': '山形',
    '福島県': '福島',
    '福島': '福島',
    '茨城県': '茨城',
    '茨城': '茨城',
    '栃木県': '栃木',
    '栃木': '栃木',
    '群馬県': '群馬',
    '群馬': '群馬',
    '埼玉県': '埼玉',
    '埼玉': '埼玉',
    '千葉県': '千葉',
    '千葉': '千葉',
    '東京都': '東京',
    '東京': '東京',
    '神奈川県': '神奈川',
    '神奈川': '神奈川',
    '新潟県': '新潟',
    '新潟': '新潟',
    '富山県': '富山',
    '富山': '富山',
    '石川県': '石川',
    '石川': '石川',
    '福井県': '福井',
    '福井': '福井',
    '山梨県': '山梨',
    '山梨': '山梨',
    '長野県': '長野',
    '長野': '長野',
    '岐阜県': '岐阜',
    '岐阜': '岐阜',
    '静岡県': '静岡',
    '静岡': '静岡',
    '愛知県': '愛知',
    '愛知': '愛知',
    '三重県': '三重',
    '三重': '三重',
    '滋賀県': '滋賀',
    '滋賀': '滋賀',
    '京都府': '京都',
    '京都': '京都',
    '大阪府': '大阪',
    '大阪': '大阪',
    '兵庫県': '兵庫',
    '兵庫': '兵庫',
    '奈良県': '奈良',
    '奈良': '奈良',
    '和歌山県': '和歌山',
    '和歌山': '和歌山',
    '鳥取県': '鳥取',
    '鳥取': '鳥取',
    '島根県': '島根',
    '島根': '島根',
    '岡山県': '岡山',
    '岡山': '岡山',
    '広島県': '広島',
    '広島': '広島',
    '山口県': '山口',
    '山口': '山口',
    '徳島県': '徳島',
    '徳島': '徳島',
    '香川県': '香川',
    '香川': '香川',
    '愛媛県': '愛媛',
    '愛媛': '愛媛',
    '高知県': '高知',
    '高知': '高知',
    '福岡県': '福岡',
    '福岡': '福岡',
    '佐賀県': '佐賀',
    '佐賀': '佐賀',
    '長崎県': '長崎',
    '長崎': '長崎',
    '熊本県': '熊本',
    '熊本': '熊本',
    '大分県': '大分',
    '大分': '大分',
    '宮崎県': '宮崎',
    '宮崎': '宮崎',
    '鹿児島県': '鹿児島',
    '鹿児島': '鹿児島',
    '沖縄県': '沖縄',
    '沖縄': '沖縄'
  };

  useEffect(() => {
    // 首先加载地图数据
    loadMapData();
  }, []);

  useEffect(() => {
    if (selectedDate && mapLoaded) {
      loadUserDistribution();
    }
  }, [selectedDate, mapLoaded]);

  useEffect(() => {
    if (chartInstance && mapData.length > 0) {
      chartInstance.setOption(option);
    }
  }, [mapData, chartInstance]);

  const loadMapData = async () => {
    try {
      // 从GitHub加载日本地图数据（这个可以显示，但是是整体日本）
      const response = await fetch('https://raw.githubusercontent.com/dataofjapan/land/master/japan.geojson');
      const japanMapData = await response.json();
      echarts.registerMap('japan', japanMapData as any);
      setMapLoaded(true);
      console.log('Loaded Japan map, features count:', japanMapData.features?.length || 0);
    } catch (error) {
      console.error('Failed to load Japan map data:', error);
      try {
        // 使用备用地图数据URL
        const response = await fetch('https://raw.githubusercontent.com/holtzy/D3-graph-gallery/master/DATA/world.geojson');
        const worldData = await response.json();
        // 过滤出日本数据
        const japanFeature = worldData.features.find((f: any) => f.properties.NAME === 'Japan');
        const japanMapData = {
          type: "FeatureCollection" as const,
          features: japanFeature ? [japanFeature] : []
        };
        echarts.registerMap('japan', japanMapData as any);
        setMapLoaded(true);
      } catch (fallbackError) {
        console.error('All map data sources failed:', fallbackError);
        setMapLoaded(true);
      }
    }
  };

  const loadUserDistribution = async () => {
    try {
      setLoading(true);
      // 获取用户地区分布数据
      const response = await analyticsApi.getUserRegionDistribution(selectedDate);
      
      // 转换为ECharts需要的数据格式
      const data: PrefectureData[] = response.regions.map(region => {
        // 尝试多种名称格式以匹配地图数据
        const possibleNames = [
          region.name,
          region.name.replace('県', ''),
          region.name.replace('都', ''),
          region.name.replace('府', ''),
          region.name.replace('道', ''),
          prefectureMapping[region.name] || region.name
        ];
        
        return {
          name: region.name, // 保留原始名称
          value: region.value,
          // 为ECharts提供多个可能的名称匹配
          nameMap: possibleNames
        };
      });
      
      // 排序并获取前三名
      const sortedData = [...data].sort((a, b) => b.value - a.value);
      const top3Names = sortedData.slice(0, 3).map(item => item.name);
      setTop3Regions(top3Names);
      
      // 为前三名设置特殊颜色
      const dataWithColors = data.map((item) => {
        const rank = top3Names.indexOf(item.name);
        if (rank === 0) {
          return { ...item, itemStyle: { areaColor: '#ff4d4f' } }; // 第一名：红色
        } else if (rank === 1) {
          return { ...item, itemStyle: { areaColor: '#ff7a45' } }; // 第二名：橙色
        } else if (rank === 2) {
          return { ...item, itemStyle: { areaColor: '#ffa940' } }; // 第三名：黄色
        }
        return { ...item, itemStyle: { areaColor: getRegionColor(item.value || 0) } };
      });

      setMapData(dataWithColors);
      console.log('地区分布数据:', dataWithColors);
    } catch (error) {
      console.error('Failed to load user distribution:', error);
      // 如果API失败，显示空数据
      setMapData([]);
    } finally {
      setLoading(false);
    }
  };

  // 获取最大值用于设置颜色范围
  const maxValue = Math.max(...mapData.map(item => item.value), 1);

  // 为柱状图准备数据 - 主要都道府县的坐标
  const prefectureCoordinates: { [key: string]: [number, number] } = {
    '北海道': [141.35, 43.06],
    '青森': [140.74, 40.82],
    '岩手': [141.15, 39.70],
    '宮城': [140.87, 38.27],
    '秋田': [140.10, 39.72],
    '山形': [140.36, 38.24],
    '福島': [140.47, 37.75],
    '茨城': [140.45, 36.34],
    '栃木': [139.88, 36.57],
    '群馬': [139.06, 36.39],
    '埼玉': [139.65, 35.86],
    '千葉': [140.12, 35.61],
    '東京': [139.69, 35.69],
    '神奈川': [139.64, 35.45],
    '新潟': [139.02, 37.90],
    '富山': [137.21, 36.70],
    '石川': [136.63, 36.59],
    '福井': [136.22, 36.06],
    '山梨': [138.57, 35.66],
    '長野': [138.18, 36.65],
    '岐阜': [136.72, 35.39],
    '静岡': [138.38, 34.98],
    '愛知': [136.91, 35.18],
    '三重': [136.51, 34.73],
    '滋賀': [135.87, 35.00],
    '京都': [135.76, 35.02],
    '大阪': [135.52, 34.69],
    '兵庫': [135.18, 34.69],
    '奈良': [135.83, 34.68],
    '和歌山': [135.17, 34.23],
    '鳥取': [134.24, 35.50],
    '島根': [133.05, 35.47],
    '岡山': [133.93, 34.66],
    '広島': [132.46, 34.40],
    '山口': [131.47, 34.19],
    '徳島': [134.56, 34.07],
    '香川': [134.04, 34.34],
    '愛媛': [132.77, 33.84],
    '高知': [133.53, 33.56],
    '福岡': [130.42, 33.61],
    '佐賀': [130.30, 33.25],
    '長崎': [129.87, 32.74],
    '熊本': [130.74, 32.79],
    '大分': [131.61, 33.24],
    '宮崎': [131.42, 31.91],
    '鹿児島': [130.56, 31.56],
    '沖縄': [127.68, 26.21]
  };

  // 获取颜色的函数
  const getRegionColor = (value: number) => {
    if (value === 0) return '#f5f5f5';
    const ratio = value / maxValue;
    if (ratio > 0.6) return '#1f77b4';
    if (ratio > 0.3) return '#5294c1';
    if (ratio > 0.1) return '#85b1ce';
    return '#b7cedb';
  };

  // ECharts配置
  // 创建名称映射表
  const nameMap: {[key: string]: string} = {};
  Object.entries(prefectureMapping).forEach(([key, value]) => {
    nameMap[value] = key; // ECharts名称 -> 数据名称
  });

  // 准备散点数据
  const scatterData = mapData.map(item => {
    const coord = prefectureCoordinates[prefectureMapping[item.name] || item.name] || [139.69, 35.69];
    return {
      name: item.name,
      value: [...coord, item.value],
      itemStyle: item.itemStyle
    };
  });

  const option = {
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        if (params.seriesType === 'scatter') {
          const userCount = params.value[2] || 0;
          return `${params.name}<br/>ユーザー数: ${userCount}人`;
        }
        return '';
      }
    },
    geo: {
      map: 'japan',
      roam: false,
      zoom: 2.0,
      center: [136, 37],
      silent: true,
      itemStyle: {
        areaColor: '#f5f5f5',
        borderColor: '#ccc',
        borderWidth: 1
      },
      emphasis: {
        disabled: true
      }
    },
    series: [
      {
        name: 'ユーザー分布',
        type: 'scatter',
        coordinateSystem: 'geo',
        data: scatterData,
        symbolSize: (val: number[]) => {
          return Math.max(10, Math.min(50, Math.sqrt(val[2]) * 5));
        },
        itemStyle: {
          shadowBlur: 10,
          shadowColor: 'rgba(0, 0, 0, 0.2)'
        },
        emphasis: {
          scale: 1.5,
          itemStyle: {
            borderColor: '#333',
            borderWidth: 2
          }
        },
        label: {
          show: true,
          formatter: '{b}',
          position: 'top',
          fontSize: 10,
          color: '#333'
        }
      }
    ]
  };

  return (
    <Card
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>ユーザー地域分布</Title>
          <span style={{ color: '#999', fontSize: '14px' }}>User Geographic Distribution</span>
        </Space>
      }
      style={{ height: '480px', overflow: 'hidden' }}
    >
      <div style={{ height: 'calc(100% - 60px)', padding: '16px' }}>
        <Spin spinning={loading || !mapLoaded}>
          {!loading && mapLoaded ? (
            <Row gutter={[16, 16]} style={{ height: '100%' }}>
              <Col xs={24} sm={14} style={{ height: '100%' }}>
                <div style={{ height: '400px', width: '100%', overflow: 'hidden' }}>
                  <ReactECharts
                    option={option}
                    style={{ height: '100%', width: '100%' }}
                    onChartReady={(chart) => {
                      setChartInstance(chart);
                    }}
                  />
                </div>
              </Col>
              <Col xs={24} sm={10} style={{ height: '100%' }}>
                <div style={{ padding: '8px 0', height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Title level={5} style={{ marginBottom: '8px', fontSize: '14px' }}>地域別ユーザー数</Title>
                  <div style={{ flex: 1, overflowY: 'auto' }}>
                    {mapData
                      .sort((a, b) => b.value - a.value)
                      .slice(0, 5)
                      .map((item, index) => (
                        <div key={item.name} style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '6px 8px',
                          background: index === 0 ? '#f6ffed' : '#fafafa',
                          borderRadius: '4px',
                          border: index === 0 ? '1px solid #b7eb8f' : '1px solid #f0f0f0',
                          marginBottom: '4px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{
                              width: '8px',
                              height: '8px',
                              backgroundColor: index === 0 ? '#ff4d4f' : index === 1 ? '#ff7a45' : index === 2 ? '#ffa940' : '#1890ff',
                              borderRadius: '2px',
                              marginRight: '4px'
                            }}></div>
                            <span style={{ fontSize: '11px', fontWeight: index < 3 ? 'bold' : 'normal' }}>
                              {index < 3 ? `🏆 ${item.name}` : item.name}
                            </span>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '12px', fontWeight: 'bold' }}>
                              {item.value}人
                            </div>
                            <div style={{ fontSize: '10px', color: '#666' }}>
                              {mapData.length > 0 ? ((item.value / mapData.reduce((sum, d) => sum + d.value, 0)) * 100).toFixed(1) : '0.0'}%
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </Col>
            </Row>
          ) : (
            <div style={{ 
              height: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              color: '#999'
            }}>
              {!mapLoaded ? '地図データを読み込み中...' : 'ユーザーデータを読み込み中...'}
            </div>
          )}
        </Spin>
      </div>
    </Card>
  );
};

export default JapanMapChart;